import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MergeFieldInserter } from '@/components/messaging/merge-field-inserter';
import {
  TemplateDialog,
  type MessageTemplateRow,
} from '@/components/messaging/template-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox, type ListboxOption } from '@/components/ui/listbox';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import {
  isReachableOnChannels,
  memberHasPhone,
} from '@/lib/messaging-contact';
import {
  hasMergeFieldPlaceholders,
  insertAtSelection,
  renderMergeFields,
  type MergeFieldValues,
} from '@/lib/messaging-merge-fields';
import { usePreferences } from '@/stores/preferences-store';
import { formatMoney } from '@/utils/format';

type Channel = 'sms';
type AudienceType =
  | 'whole_church'
  | 'department'
  | 'unit'
  | 'group'
  | 'committee'
  | 'auto_group'
  | 'council'
  | 'list'
  | 'manual';
type SendTiming = 'now' | 'schedule';
type RepeatInterval = 'none' | 'daily' | 'weekly' | 'monthly';

interface MessageTemplate {
  id: string;
  name: string;
  subjectPush: string | null;
  bodyPush: string | null;
  bodySms: string | null;
}

interface MemberSearchRow {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  memberType: 'first_timer' | 'visitor' | 'member';
}

interface SelectedMember {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  memberType: 'first_timer' | 'visitor' | 'member';
}

const CREATE_TEMPLATE_VALUE = '__create_message_template__';

const toSelectedMember = (row: MemberSearchRow): SelectedMember => ({
  id: row.id,
  firstName: row.firstName,
  lastName: row.lastName,
  phone: row.phone ?? null,
  email: row.email ?? null,
  memberType: row.memberType,
});

export interface ComposeDraft {
  /** When set, save/send updates this campaign instead of creating a new one. */
  campaignId?: string;
  name?: string;
  subject?: string | null;
  body?: string;
  channels?: Channel[];
  audienceType?: AudienceType;
  audienceRef?: string | null;
  audienceMemberIds?: string[] | null;
  templateId?: string | null;
  scheduledAt?: string | null;
  repeatInterval?: RepeatInterval;
  repeatUntil?: string | null;
  /** Drafts open as Send; already-sent campaigns open as Send again. */
  reuseKind?: 'send' | 'resend';
}

interface ComposeMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Prefill for resend / reuse. Cleared when the dialog closes. */
  draft?: ComposeDraft | null;
}

/** Matches server `SMS_SEGMENT_LENGTH` for cost and length guidance. */
const SMS_SEGMENT_LENGTH = 160;

const smsSegmentCount = (text: string): number => {
  if (!text.length) return 0;
  return Math.max(1, Math.ceil(text.length / SMS_SEGMENT_LENGTH));
};

export const ComposeMessageDialog = ({
  open,
  onOpenChange,
  draft = null,
}: ComposeMessageDialogProps) => {
  const { t } = useTranslation('messaging');
  const { t: tTips } = useTranslation('tips');
  const { t: tCommon } = useTranslation();
  const { t: tPeople } = useTranslation('people');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const churchTimezone = usePreferences((state) => state.timezone);

  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const channels: Channel[] = ['sms'];
  const [audienceType, setAudienceType] = useState<AudienceType>('whole_church');
  const [audienceRef, setAudienceRef] = useState<string | null>(null);
  const [audienceDepartmentId, setAudienceDepartmentId] = useState<string | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<SelectedMember[]>([]);
  const [draftMemberIds, setDraftMemberIds] = useState<string[]>([]);
  const [memberQuery, setMemberQuery] = useState('');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [templateCreateOpen, setTemplateCreateOpen] = useState(false);
  const [sendTiming, setSendTiming] = useState<SendTiming>('now');
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  const [scheduleInPast, setScheduleInPast] = useState(false);
  const [repeatInterval, setRepeatInterval] = useState<RepeatInterval>('none');
  const [repeatUntil, setRepeatUntil] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [bodySelection, setBodySelection] = useState({ start: 0, end: 0 });

  const minScheduleDate = DateTime.now().setZone(churchTimezone).toFormat('yyyy-MM-dd');

  const rememberBodySelection = () => {
    const el = bodyRef.current;
    if (!el) return;
    setBodySelection({ start: el.selectionStart ?? body.length, end: el.selectionEnd ?? body.length });
  };

  const insertIntoBody = (token: string) => {
    const { value: next, caret } = insertAtSelection(
      body,
      bodySelection.start,
      bodySelection.end,
      token,
    );
    setBody(next);
    setBodySelection({ start: caret, end: caret });
    requestAnimationFrame(() => {
      bodyRef.current?.focus();
      bodyRef.current?.setSelectionRange(caret, caret);
    });
  };

  const setScheduleTime = (utcIso: string | null) => {
    if (!utcIso) {
      setScheduledAt(null);
      setScheduleInPast(false);
      return;
    }
    if (DateTime.fromISO(utcIso, { zone: 'utc' }) <= DateTime.utc()) {
      setScheduledAt(null);
      setScheduleInPast(true);
      return;
    }
    setScheduledAt(utcIso);
    setScheduleInPast(false);
  };

  const memberIds = selectedMembers.map((row) => row.id);

  useEffect(() => {
    if (!open) return;
    if (draft) {
      setName(draft.name ?? '');
      setBody(draft.body ?? '');
      setAudienceType(draft.audienceType ?? 'whole_church');
      setAudienceRef(draft.audienceRef ?? null);
      setAudienceDepartmentId(null);
      setSelectedMembers([]);
      setDraftMemberIds(draft.audienceMemberIds ?? []);
      setTemplateId(draft.templateId ?? null);
      setSendTiming(draft.scheduledAt ? 'schedule' : 'now');
      setScheduledAt(draft.scheduledAt ?? null);
      setRepeatInterval(draft.repeatInterval ?? 'none');
      setRepeatUntil(draft.repeatUntil ?? null);
    } else {
      setName('');
      setBody('');
      setAudienceType('whole_church');
      setAudienceRef(null);
      setAudienceDepartmentId(null);
      setSelectedMembers([]);
      setDraftMemberIds([]);
      setTemplateId(null);
      setSendTiming('now');
      setScheduledAt(null);
      setRepeatInterval('none');
      setRepeatUntil(null);
    }
    setMemberQuery('');
    setScheduleInPast(false);
  }, [open, draft]);

  const departments = useQuery({
    queryKey: ['messaging', 'departments'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>('/people/departments?pageSize=100'),
    enabled: open && (audienceType === 'department' || audienceType === 'unit'),
  });

  const units = useQuery({
    queryKey: ['messaging', 'department-units', audienceDepartmentId],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; label: string; name: string }> }>(
        `/people/department-units?pageSize=100&departmentId=${encodeURIComponent(audienceDepartmentId ?? '')}`,
      ),
    enabled: open && audienceType === 'unit' && Boolean(audienceDepartmentId),
  });

  const groups = useQuery({
    queryKey: ['messaging', 'groups', 'manual'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>(
        '/groups?pageSize=100&type=manual&category=group',
      ),
    enabled: open && audienceType === 'group',
  });

  const committees = useQuery({
    queryKey: ['messaging', 'committees'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>(
        '/groups?pageSize=100&type=manual&category=committee',
      ),
    enabled: open && audienceType === 'committee',
  });

  const autoGroups = useQuery({
    queryKey: ['messaging', 'auto-groups'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>('/groups?pageSize=100&type=template'),
    enabled: open && audienceType === 'auto_group',
  });

  const lists = useQuery({
    queryKey: ['messaging', 'lists-options'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>('/messaging/lists?pageSize=100'),
    enabled: open && audienceType === 'list',
  });

  const members = useQuery({
    queryKey: ['messaging', 'member-search', memberQuery],
    queryFn: () =>
      api.get<{ data: MemberSearchRow[] }>(
        `/people/members?pageSize=50&status=active${
          memberQuery ? `&q=${encodeURIComponent(memberQuery)}` : ''
        }`,
      ),
    enabled: open && audienceType === 'manual',
  });

  const draftMembers = useQuery({
    queryKey: ['messaging', 'compose-draft-members', draftMemberIds],
    queryFn: async () => {
      const rows = await Promise.all(
        draftMemberIds.map((id) => api.get<MemberSearchRow>(`/people/members/${id}`)),
      );
      return rows.map(toSelectedMember);
    },
    enabled: open && audienceType === 'manual' && draftMemberIds.length > 0,
  });

  useEffect(() => {
    if (!draftMembers.data?.length) return;
    setSelectedMembers(draftMembers.data);
    setDraftMemberIds([]);
  }, [draftMembers.data]);

  const templates = useQuery({
    queryKey: ['messaging', 'templates'],
    queryFn: () => api.get<{ data: MessageTemplate[] }>('/messaging/templates?pageSize=100'),
    enabled: open,
  });

  const audienceReady =
    audienceType === 'whole_church' ||
    audienceType === 'council' ||
    (audienceType === 'manual' ? memberIds.length > 0 : Boolean(audienceRef));

  const preview = useQuery({
    queryKey: ['messaging', 'preview', channels, audienceType, audienceRef, memberIds, body],
    queryFn: () =>
      api.post<{
        totalRecipients: number;
        emailCount: number;
        smsCount: number;
        estimatedCostMinor: number;
        currency: string;
        balanceMinor: number;
        sufficientCredits: boolean;
        smsChargesCredits: boolean;
        smsConfigured: boolean;
        sampleRecipientName: string | null;
        sampleMergeValues: MergeFieldValues | null;
      }>('/messaging/audience/preview', {
        audienceType,
        audienceRef,
        audienceMemberIds: audienceType === 'manual' ? memberIds : undefined,
        channels,
        body: body || ' ',
      }),
    enabled: open && channels.length > 0 && audienceReady,
  });

  const previewBody = useMemo(() => {
    if (!body.trim()) return null;
    if (!hasMergeFieldPlaceholders(body)) return null;
    if (!preview.data?.sampleMergeValues) return null;
    return renderMergeFields(body, preview.data.sampleMergeValues);
  }, [body, preview.data?.sampleMergeValues]);

  const previewLabel = useMemo(() => {
    const name = preview.data?.sampleRecipientName;
    if (!name || !previewBody) return null;
    const total = preview.data?.totalRecipients ?? 0;
    if (total <= 1) return t('campaigns.messagePreviewFor', { name });
    return t('campaigns.messagePreviewExample', { name, total });
  }, [preview.data?.sampleRecipientName, preview.data?.totalRecipients, previewBody, t]);

  const campaignPayload = () => ({
    name,
    subject: null,
    body,
    channels,
    audienceType,
    audienceRef,
    audienceMemberIds: audienceType === 'manual' ? memberIds : undefined,
    templateId,
    scheduledAt: sendTiming === 'schedule' ? scheduledAt : null,
    repeatInterval: sendTiming === 'schedule' ? repeatInterval : 'none',
    repeatUntil:
      sendTiming === 'schedule' && repeatInterval !== 'none' ? repeatUntil : null,
  });

  const saveDraft = useMutation({
    mutationFn: () => {
      const payload = {
        ...campaignPayload(),
        scheduledAt: null,
      };
      if (draft?.campaignId) {
        return api.patch(`/messaging/campaigns/${draft.campaignId}`, payload);
      }
      return api.post('/messaging/campaigns', payload);
    },
    onSuccess: () => {
      toast.success(t('campaigns.draftSaved'));
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'campaigns'] });
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'insights'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const saveAsTemplate = useMutation({
    mutationFn: () =>
      api.post('/messaging/templates', {
        name: name.trim(),
        subjectPush: null,
        bodyPush: null,
        bodySms: body.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t('templates.saved'));
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'templates'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const scheduleOrSend = useMutation({
    mutationFn: async () => {
      const payload = campaignPayload();
      const campaign = draft?.campaignId
        ? await api.patch<{ id: string }>(`/messaging/campaigns/${draft.campaignId}`, payload)
        : await api.post<{ id: string }>('/messaging/campaigns', payload);
      if (sendTiming === 'schedule' && scheduledAt) {
        return { kind: 'scheduled' as const, campaign };
      }
      const sent = await api.post<{
        id: string;
        status: string;
        deliveredCount: number;
        failedCount: number;
      }>(`/messaging/campaigns/${campaign.id}/send`, {});
      return { kind: 'sent' as const, campaign: sent };
    },
    onSuccess: (result) => {
      if (result.kind === 'scheduled') {
        toast.success(t('campaigns.scheduled'));
      } else if (result.campaign.status === 'failed') {
        toast.error(t('campaigns.sendFailed'));
      } else if (result.campaign.status === 'partially_failed') {
        toast.warning(t('campaigns.sendPartial'));
      } else {
        toast.success(t('campaigns.sent'));
      }
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'campaigns'] });
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'insights'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const fillFromTemplate = (template: {
    id: string;
    name: string;
    subjectPush: string | null;
    bodyPush: string | null;
    bodySms: string | null;
  }) => {
    setTemplateId(template.id);
    setBody(template.bodySms || template.bodyPush || '');
    if (!name.trim()) setName(template.name);
  };

  const applyTemplate = (id: string | null) => {
    if (id === CREATE_TEMPLATE_VALUE) {
      setTemplateCreateOpen(true);
      return;
    }
    if (!id) {
      setTemplateId(null);
      return;
    }
    const template = templates.data?.data.find((row) => row.id === id);
    if (!template) return;
    fillFromTemplate(template);
  };

  const templateOptions: ListboxOption[] = [
    ...(templates.data?.data.map((row) => ({
      value: row.id,
      label: row.name,
    })) ?? []),
    {
      value: CREATE_TEMPLATE_VALUE,
      label: t('templates.createInline'),
      description: tTips('messaging.templateCreateInline'),
      icon: <Plus className="size-4" aria-hidden />,
    },
  ];

  const memberOptions =
    members.data?.data
      .filter((row) => !selectedMembers.some((selected) => selected.id === row.id))
      .map((row) => ({
        id: row.id,
        label: `${row.firstName} ${row.lastName}`.trim(),
      })) ?? [];

  const missingPhoneCount = selectedMembers.filter((row) => !memberHasPhone(row)).length;
  const manualReachableCount = selectedMembers.filter((row) =>
    isReachableOnChannels(row, channels),
  ).length;
  const manualNoneReachable =
    audienceType === 'manual' && selectedMembers.length > 0 && manualReachableCount === 0;
  const previewNoneReachable =
    audienceType !== 'manual' &&
    audienceReady &&
    preview.data != null &&
    preview.data.totalRecipients === 0;

  const selectedColumns: DataTableColumn<SelectedMember>[] = [
    {
      id: 'name',
      header: t('lists.selectedColumns.name'),
      cell: (row) => (
        <MemberNameLink
          memberId={row.id}
          name={`${row.firstName} ${row.lastName}`.trim()}
        />
      ),
    },
    {
      id: 'type',
      header: t('lists.selectedColumns.type'),
      cell: (row) => tPeople(`members.type.${row.memberType}`),
      hideBelowMd: true,
    },
    {
      id: 'phone',
      header: t('lists.selectedColumns.phone'),
      cell: (row) => {
        if (memberHasPhone(row)) return row.phone;
        return <span className="text-danger">—</span>;
      },
      hideBelowMd: true,
    },
  ];

  const repeatUntilInvalid = Boolean(
    sendTiming === 'schedule' &&
      repeatInterval !== 'none' &&
      scheduledAt &&
      repeatUntil &&
      repeatUntil <
        DateTime.fromISO(scheduledAt, { zone: 'utc' }).setZone(churchTimezone).toISODate()!,
  );

  const insufficientCredits = Boolean(
    preview.data?.smsChargesCredits &&
      preview.data.smsConfigured &&
      preview.data.estimatedCostMinor > 0 &&
      !preview.data.sufficientCredits,
  );

  const canSubmit =
    name.trim() &&
    body.trim() &&
    (audienceType === 'whole_church' ||
      audienceType === 'council' ||
      (audienceType === 'manual' ? selectedMembers.length > 0 : Boolean(audienceRef))) &&
    !manualNoneReachable &&
    !previewNoneReachable &&
    (sendTiming === 'now' ||
      (Boolean(scheduledAt) &&
        DateTime.fromISO(scheduledAt!, { zone: 'utc' }) > DateTime.utc() &&
        (repeatInterval === 'none' || Boolean(repeatUntil)) &&
        !repeatUntilInvalid)) &&
    !(sendTiming === 'now' && insufficientCredits);

  const pending =
    saveDraft.isPending || scheduleOrSend.isPending || saveAsTemplate.isPending;
  const segments = smsSegmentCount(body);
  const smsRecipients = preview.data?.smsCount ?? 0;
  const totalSmsParts = segments > 0 ? segments * Math.max(smsRecipients, 0) : 0;
  const showAudiencePicker =
    audienceType === 'department' ||
    audienceType === 'unit' ||
    audienceType === 'group' ||
    audienceType === 'committee' ||
    audienceType === 'auto_group' ||
    audienceType === 'list' ||
    audienceType === 'manual';

  const audiencePickerField =
    audienceType === 'department' ? (
      <div>
        <Label>{t('campaigns.selectDepartment')}</Label>
        <Listbox
          value={audienceRef}
          onChange={setAudienceRef}
          options={
            departments.data?.data.map((row) => ({
              value: row.id,
              label: row.name,
            })) ?? []
          }
          popoverPortal="dialog"
        />
      </div>
    ) : audienceType === 'unit' ? (
      <>
        <div>
          <Label>{t('campaigns.selectDepartment')}</Label>
          <Listbox
            value={audienceDepartmentId}
            onChange={(value) => {
              setAudienceDepartmentId(value);
              setAudienceRef(null);
            }}
            options={
              departments.data?.data.map((row) => ({
                value: row.id,
                label: row.name,
              })) ?? []
            }
            popoverPortal="dialog"
          />
        </div>
        <div>
          <Label>{t('campaigns.selectUnit')}</Label>
          <Listbox
            value={audienceRef}
            onChange={setAudienceRef}
            options={
              units.data?.data.map((row) => ({
                value: row.id,
                label: row.label ?? row.name,
              })) ?? []
            }
            placeholder={
              audienceDepartmentId ? t('campaigns.unitPlaceholder') : t('campaigns.selectDepartmentFirst')
            }
            disabled={!audienceDepartmentId}
            popoverPortal="dialog"
          />
        </div>
      </>
    ) : audienceType === 'group' ? (
      <div>
        <Label>{t('campaigns.selectGroup')}</Label>
        <Listbox
          value={audienceRef}
          onChange={setAudienceRef}
          options={
            groups.data?.data.map((row) => ({
              value: row.id,
              label: row.name,
            })) ?? []
          }
          popoverPortal="dialog"
        />
      </div>
    ) : audienceType === 'committee' ? (
      <div>
        <Label>{t('campaigns.selectCommittee')}</Label>
        <Listbox
          value={audienceRef}
          onChange={setAudienceRef}
          options={
            committees.data?.data.map((row) => ({
              value: row.id,
              label: row.name,
            })) ?? []
          }
          popoverPortal="dialog"
        />
      </div>
    ) : audienceType === 'auto_group' ? (
      <div>
        <Label>{t('campaigns.selectAutoGroup')}</Label>
        <Listbox
          value={audienceRef}
          onChange={setAudienceRef}
          options={
            autoGroups.data?.data.map((row) => ({
              value: row.id,
              label: row.name,
            })) ?? []
          }
          popoverPortal="dialog"
        />
      </div>
    ) : audienceType === 'list' ? (
      <div>
        <Label>{t('campaigns.selectList')}</Label>
        <Listbox
          value={audienceRef}
          onChange={setAudienceRef}
          options={
            lists.data?.data.map((row) => ({
              value: row.id,
              label: row.name,
            })) ?? []
          }
          popoverPortal="dialog"
        />
      </div>
    ) : audienceType === 'manual' ? (
      <div>
        <Label>{t('campaigns.selectMembers')}</Label>
        <EntityCombobox
          value={null}
          onChange={(id) => {
            if (!id) return;
            const row = members.data?.data.find((member) => member.id === id);
            if (!row) return;
            setSelectedMembers((current) =>
              current.some((member) => member.id === id)
                ? current
                : [...current, toSelectedMember(row)],
            );
          }}
          options={memberOptions}
          onQueryChange={setMemberQuery}
          loading={members.isPending}
          placeholder={t('lists.searchMembers')}
          popoverPortal="dialog"
          clearLabel={tCommon('action.clearSelection')}
        />
      </div>
    ) : null;

  const whenToSendField = (
    <div>
      <Label>{t('campaigns.whenToSend')}</Label>
      <Listbox
        value={sendTiming}
        onChange={(value) => {
          const next = (value as SendTiming) ?? 'now';
          setSendTiming(next);
          if (next === 'now') {
            setScheduledAt(null);
            setRepeatInterval('none');
            setRepeatUntil(null);
          }
        }}
        options={[
          { value: 'now', label: t('campaigns.sendNow') },
          { value: 'schedule', label: t('campaigns.schedule') },
        ]}
        popoverPortal="dialog"
      />
    </div>
  );

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>
            {draft
              ? draft.reuseKind === 'send'
                ? t('campaigns.sendTitle')
                : t('campaigns.resendTitle')
              : t('campaigns.create')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="campaign-name">{t('campaigns.name')}</Label>
                <Input id="campaign-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <Label>{t('campaigns.template')}</Label>
                <Listbox
                  value={templateId}
                  onChange={applyTemplate}
                  options={templateOptions}
                  placeholder={t('campaigns.templatePlaceholder')}
                  emptyMessage={t('templates.emptyPicker')}
                  clearable
                  clearLabel={tCommon('action.clearSelection')}
                  popoverPortal="dialog"
                />
              </div>
              <div>
                <Label>{t('campaigns.audience')}</Label>
                <Listbox
                  value={audienceType}
                  onChange={(value) => {
                    setAudienceType((value as AudienceType) ?? 'whole_church');
                    setAudienceRef(null);
                    setAudienceDepartmentId(null);
                    setSelectedMembers([]);
                    setDraftMemberIds([]);
                  }}
                  options={[
                    { value: 'whole_church', label: t('campaigns.audienceWholeChurch') },
                    { value: 'department', label: t('campaigns.audienceDepartment') },
                    { value: 'unit', label: t('campaigns.audienceUnit') },
                    { value: 'group', label: t('campaigns.audienceGroup') },
                    { value: 'committee', label: t('campaigns.audienceCommittee') },
                    { value: 'auto_group', label: t('campaigns.audienceAutoGroup') },
                    { value: 'council', label: t('campaigns.audienceCouncil') },
                    { value: 'list', label: t('campaigns.audienceList') },
                    { value: 'manual', label: t('campaigns.audienceManual') },
                  ]}
                  popoverPortal="dialog"
                />
              </div>
              {showAudiencePicker ? audiencePickerField : null}
              {whenToSendField}
            </div>

            {sendTiming === 'schedule' ? (
              <div className="space-y-4">
                <div>
                  <Label>{t('campaigns.scheduleAt')}</Label>
                  <DateTimePicker
                    value={scheduledAt}
                    onChange={setScheduleTime}
                    minDate={minScheduleDate}
                    invalid={scheduleInPast}
                    popoverPortal="dialog"
                    timezoneNotice={(tz) => t('campaigns.scheduleTimezone', { timezone: tz })}
                  />
                  <Tip tipId="messaging.scheduleTimezone" />
                  {scheduleInPast ? (
                    <p className="mt-1 text-caption text-danger">
                      {t('campaigns.scheduleMustBeFuture')}
                    </p>
                  ) : null}
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label>{t('campaigns.repeat')}</Label>
                    <Listbox
                      value={repeatInterval}
                      onChange={(value) => {
                        const next = (value as RepeatInterval) ?? 'none';
                        setRepeatInterval(next);
                        if (next === 'none') setRepeatUntil(null);
                      }}
                      options={[
                        { value: 'none', label: t('campaigns.repeatNone') },
                        { value: 'daily', label: t('campaigns.repeatDaily') },
                        { value: 'weekly', label: t('campaigns.repeatWeekly') },
                        { value: 'monthly', label: t('campaigns.repeatMonthly') },
                      ]}
                      popoverPortal="dialog"
                    />
                  </div>
                  {repeatInterval !== 'none' ? (
                    <div>
                      <Label>{t('campaigns.repeatUntil')}</Label>
                      <DatePicker
                        value={repeatUntil}
                        onChange={setRepeatUntil}
                        min={minScheduleDate}
                        invalid={repeatUntilInvalid}
                        popoverPortal="dialog"
                      />
                      {repeatUntilInvalid ? (
                        <p className="mt-1 text-caption text-danger">
                          {t('campaigns.repeatUntilInvalid')}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div>
              <Label htmlFor="campaign-body">{t('campaigns.body')}</Label>
              <Textarea
                ref={bodyRef}
                id="campaign-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onSelect={rememberBodySelection}
                onKeyUp={rememberBodySelection}
                onClick={rememberBodySelection}
                onBlur={rememberBodySelection}
                rows={5}
              />
              <MergeFieldInserter onInsert={insertIntoBody} />
              <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-caption text-fg-secondary">
                <Tip tipId="messaging.smsLength" />
                <p>
                  {t('campaigns.smsLength', {
                    chars: body.length,
                    segments,
                  })}
                </p>
              </div>
              {segments > 1 ? (
                <p className="mt-1 text-caption text-fg-secondary">
                  {t('campaigns.smsMultiPart', { segments })}
                </p>
              ) : null}
              {previewBody ? (
                <div className="mt-3 rounded-md border border-border bg-surface-secondary px-3 py-2">
                  <p className="text-caption font-medium text-fg-secondary">
                    {t('campaigns.messagePreview')}
                  </p>
                  {previewLabel ? (
                    <p className="mt-0.5 text-caption text-fg-secondary">{previewLabel}</p>
                  ) : null}
                  <p className="mt-1 whitespace-pre-wrap break-words text-body-sm text-fg">
                    {previewBody}
                  </p>
                </div>
              ) : null}
              {preview.data && smsRecipients > 0 ? (
                <p className="mt-1 text-caption text-fg-secondary">
                  {t('campaigns.smsTotalParts', {
                    parts: totalSmsParts,
                    people: smsRecipients,
                  })}
                </p>
              ) : null}
            </div>

            {preview.data ? (
              <div className="space-y-1 text-body-sm text-fg-secondary">
                <p>{t('campaigns.previewSummary', { total: preview.data.totalRecipients })}</p>
                {!preview.data.smsConfigured ? (
                  <p>{t('campaigns.smsNotConfigured')}</p>
                ) : preview.data.smsChargesCredits ? (
                  <p>
                    {t('campaigns.previewCost', {
                      amount: formatMoney(
                        preview.data.estimatedCostMinor,
                        preview.data.currency,
                      ),
                      smsCount: preview.data.smsCount,
                    })}
                  </p>
                ) : (
                  <p>{t('campaigns.previewByok')}</p>
                )}
                {insufficientCredits ? (
                  <p className="text-danger" role="status">
                    {t(
                      sendTiming === 'schedule'
                        ? 'campaigns.insufficientCreditsSchedule'
                        : 'campaigns.insufficientCredits',
                      {
                        balance: formatMoney(
                          preview.data.balanceMinor,
                          preview.data.currency,
                        ),
                        amount: formatMoney(
                          preview.data.estimatedCostMinor,
                          preview.data.currency,
                        ),
                      },
                    )}
                  </p>
                ) : null}
              </div>
            ) : null}

            {audienceType === 'manual' ? (
              <div>
                <Label>{t('campaigns.selectedPeople')}</Label>
                <div className="mt-2">
                  <DataTable
                    rows={selectedMembers}
                    columns={selectedColumns}
                    rowKey={(row) => row.id}
                    empty={
                      <p className="text-body-sm text-fg-secondary">
                        {t('campaigns.noPeopleSelected')}
                      </p>
                    }
                    actionsHeader={tCommon('table.actions')}
                    rowActions={(row) => (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          setSelectedMembers((current) =>
                            current.filter((member) => member.id !== row.id),
                          )
                        }
                      >
                        {tCommon('action.remove')}
                      </Button>
                    )}
                  />
                </div>
                <p className="mt-1 text-caption text-fg-secondary">
                  {t('campaigns.recipients', { count: selectedMembers.length })}
                </p>
                {manualNoneReachable ? (
                  <p className="mt-2 text-body-sm text-danger" role="status">
                    {t('campaigns.noneReachable')}
                  </p>
                ) : (
                  <>
                    {missingPhoneCount > 0 ? (
                      <p className="mt-2 text-body-sm text-warning" role="status">
                        {t('campaigns.unreachableNoPhone', { count: missingPhoneCount })}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            ) : null}

            {previewNoneReachable ? (
              <p className="text-body-sm text-danger" role="status">
                {t('campaigns.noneReachable')}
              </p>
            ) : null}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {tCommon('action.cancel')}
          </Button>
          <Button
            variant="secondary"
            disabled={!name.trim() || !body.trim() || pending}
            onClick={() => saveAsTemplate.mutate()}
          >
            {t('campaigns.saveAsTemplate')}
          </Button>
          <Button
            variant="secondary"
            disabled={!canSubmit || pending}
            onClick={() => saveDraft.mutate()}
          >
            {t('campaigns.saveDraft')}
          </Button>
          <Button
            disabled={!canSubmit || pending}
            onClick={() => scheduleOrSend.mutate()}
          >
            {sendTiming === 'schedule'
              ? t('campaigns.scheduleSend')
              : t('campaigns.confirmSend')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <TemplateDialog
      open={templateCreateOpen}
      onOpenChange={setTemplateCreateOpen}
      defaults={{
        name: name.trim() || undefined,
        bodySms: body.trim() || null,
      }}
      onSaved={(saved: MessageTemplateRow) => {
        fillFromTemplate(saved);
      }}
    />
    </>
  );
};
